import * as React from 'react';
import {cn} from 'src/lib/utils';

/** Any value that can come out of JSON.parse. */
export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | {[key: string]: JsonValue};

const INDENT = '  ';

const Punctuation = ({children}: {children: React.ReactNode}) => (
  <span className="text-muted-foreground">{children}</span>
);

const renderScalar = (value: string | number | boolean | null) => {
  if (value === null) {
    return <span className="text-muted-foreground italic">null</span>;
  }
  switch (typeof value) {
    case 'string':
      // JSON.stringify quotes AND escapes — a value containing `"` or a
      // newline must render as valid JSON, not alter the apparent structure.
      return (
        <span className="text-emerald-700 dark:text-emerald-400">
          {JSON.stringify(value)}
        </span>
      );
    case 'number':
      return <span className="text-sky-700 dark:text-sky-400">{value}</span>;
    default:
      return (
        <span className="text-purple-700 dark:text-purple-400">
          {String(value)}
        </span>
      );
  }
};

const renderValue = (value: JsonValue, depth: number): React.ReactNode => {
  if (value === null || typeof value !== 'object') {
    return renderScalar(value);
  }

  const pad = INDENT.repeat(depth + 1);
  const closePad = INDENT.repeat(depth);

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <Punctuation>[]</Punctuation>;
    }
    return (
      <>
        <Punctuation>[</Punctuation>
        {'\n'}
        {value.map((item, index) => (
          <React.Fragment key={index}>
            {pad}
            {renderValue(item, depth + 1)}
            {index < value.length - 1 && <Punctuation>,</Punctuation>}
            {'\n'}
          </React.Fragment>
        ))}
        {closePad}
        <Punctuation>]</Punctuation>
      </>
    );
  }

  const entries = Object.entries(value);
  if (entries.length === 0) {
    return <Punctuation>{'{}'}</Punctuation>;
  }
  return (
    <>
      <Punctuation>{'{'}</Punctuation>
      {'\n'}
      {entries.map(([key, item], index) => (
        <React.Fragment key={key}>
          {pad}
          <span className="text-foreground font-medium">
            {JSON.stringify(key)}
          </span>
          <Punctuation>: </Punctuation>
          {renderValue(item, depth + 1)}
          {index < entries.length - 1 && <Punctuation>,</Punctuation>}
          {'\n'}
        </React.Fragment>
      ))}
      {closePad}
      <Punctuation>{'}'}</Punctuation>
    </>
  );
};

type Props = {
  value: JsonValue;
  className?: string;
};

/**
 * Pretty-printed, syntax-colored JSON. Purely presentational: renders the
 * value with 2-space indentation, colored per token type, inside a
 * horizontally scrollable <pre>.
 */
export const JsonTree = ({value, className}: Props) => (
  <pre
    className={cn(
      'm-0 overflow-x-auto font-mono text-xs leading-relaxed whitespace-pre',
      className,
    )}
  >
    {renderValue(value, 0)}
  </pre>
);
